import React from "react";
import { Robot } from "@phosphor-icons/react";
import { CcPanel, SubBlock } from "./CcPanel";
import { C, PERIODS, aud, num, pct } from "./ccUtils";

const MODE = {
  shadow: { label: "Suggestions only", color: C.green },
  paused: { label: "Emergency pause", color: C.amber },
  off: { label: "Off", color: "#a3a3a3" },
};
const DECISIONS = [["approve", "Approve", C.green], ["review", "Needs a person", C.sky], ["further_info", "Further info", C.amber], ["decline", "Decline", C.red]];

function ModeChip({ ai }) {
  const m = MODE[ai.mode];
  const label = ai.mode === "off" && !ai.validated ? "Off · awaiting validation" : m.label;
  return (
    <span data-testid="ai-mode-chip" data-mode={ai.mode} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5"
      style={{ color: m.color, borderColor: `${m.color}55`, background: `${m.color}14` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />{label}
    </span>
  );
}

function Assessed({ assessed }) {
  return (
    <SubBlock title="Claims assessed" testid="ai-assessed">
      <div className="space-y-1">
        {PERIODS.map((p) => (
          <div key={p.key} className="flex items-baseline justify-between" data-testid={`ai-assessed-${p.key}`}>
            <span className="text-[11px] text-neutral-500">{p.label}</span>
            <span className="font-heading text-lg font-bold text-white tabular-nums leading-tight">{num(assessed[p.key])}</span>
          </div>
        ))}
      </div>
    </SubBlock>
  );
}

function Decisions({ by, auto }) {
  const total = Math.max(1, ...DECISIONS.map(([k]) => by[k] || 0));
  return (
    <SubBlock title="AI suggestions" testid="ai-decisions">
      <div className="space-y-1.5 flex-1">
        {DECISIONS.map(([k, label, c]) => (
          <div key={k} data-testid={`ai-decision-${k}`}>
            <div className="flex justify-between text-[11px]"><span className="text-neutral-400">{label}</span><span className="font-mono text-white">{num(by[k] || 0)}</span></div>
            <div className="h-1 rounded-full bg-neutral-800 mt-0.5"><div className="h-full rounded-full" style={{ width: `${((by[k] || 0) / total) * 100}%`, background: c }} /></div>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-neutral-500 mt-2" data-testid="ai-would-auto">Would auto-approve: <span className="text-white font-mono">{num(auto)}</span></div>
    </SubBlock>
  );
}

function Accuracy({ cal }) {
  return (
    <SubBlock title="Tested on past decisions" testid="ai-accuracy">
      {cal.n ? (
        <div className="space-y-2">
          <div><div className="font-heading text-[26px] font-bold leading-none" style={{ color: C.green }} data-testid="ai-safe-pct">{pct(cal.safe_pct)}</div>
            <div className="text-[10px] text-neutral-500 mt-1">safe calls · matched or sent to a person</div></div>
          <div><div className="font-heading text-lg font-bold text-white leading-none" data-testid="ai-agree-pct">{pct(cal.ai_lean_agree_pct)}</div>
            <div className="text-[10px] text-neutral-500 mt-1">AI judgement matched the team</div></div>
          <div className="text-[10px] font-mono text-neutral-600">{num(cal.n)} past claims tested</div>
        </div>
      ) : <div className="text-xs text-neutral-600">Not tested yet</div>}
    </SubBlock>
  );
}

function Automation({ a }) {
  const stats = [["Approved in OTM TECH", num(a.approved_in_app), "ai-approved-in-app"], ["AI auto-approved", num(a.auto_approved), "ai-auto-approved"],
    ["Hours saved", `${a.hours_saved}`, "ai-hours-saved"], ["Labour saved", aud(a.labour_saved), "ai-labour-saved"]];
  return (
    <div className="rounded-lg border border-dashed border-neutral-800 px-3 py-2 flex items-center gap-5 shrink-0" data-testid="ai-automation">
      {stats.map(([l, v, id]) => (
        <div key={id} data-testid={id}>
          <div className="font-heading text-base font-bold tabular-nums leading-tight" style={{ color: id === "ai-labour-saved" ? C.gold : C.white }}>{v}</div>
          <div className="text-[10px] text-neutral-500 whitespace-nowrap">{l}</div>
        </div>
      ))}
      <p className="ml-auto text-[10px] text-neutral-500 leading-snug max-w-[290px] text-right" data-testid="ai-automation-note">
        Each claim approved in OTM TECH{a.live ? " or auto-approved by the AI" : ""} counts as {a.minutes_per_claim} min saved{a.parts_minutes ? `, +${a.parts_minutes} min when parts are ordered` : ""} · {aud(a.rate, true)}/hr
      </p>
    </div>
  );
}

export function AiPanel({ ai }) {
  return (
    <CcPanel title="AI warranty assessment" icon={Robot} testid="panel-ai-assessment-mode" className="flex-1" bodyClass="flex flex-col gap-2.5" delay={0.2}
      aside={<ModeChip ai={ai} />}>
      <div className="grid grid-cols-3 gap-2.5 flex-1 min-h-0">
        <Assessed assessed={ai.assessed} />
        <Decisions by={ai.by_decision} auto={ai.would_auto_approve} />
        <Accuracy cal={ai.calibration} />
      </div>
      <Automation a={ai.automation} />
    </CcPanel>
  );
}
